import { useMemo, useReducer } from 'react';
import type { IPolicyFilterState, IInsurancePolicy } from '@/@types/insurance';

type FilterAction = // LOCAL_TYPE_JUSTIFIED: reducer action types used only in this hook
  | { type: 'SET_SEARCH'; query: string }
  | { type: 'SET_TYPE_FILTER'; typeFilter: string }
  | { type: 'SET_STATUS_FILTER'; statusFilter: string }
  | { type: 'SET_INVOICE_FILTER'; invoiceFilter: string }
  | { type: 'SET_RENEWAL_FILTER'; renewalFilter: string };

const initialFilterState: IPolicyFilterState = {
  searchQuery: '',
  typeFilter: 'all',
  statusFilter: 'all',
  invoiceFilter: 'all',
  renewalFilter: 'all',
};

function filterReducer(state: IPolicyFilterState, action: FilterAction): IPolicyFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.query };
    case 'SET_TYPE_FILTER':
      return { ...state, typeFilter: action.typeFilter };
    case 'SET_STATUS_FILTER':
      return { ...state, statusFilter: action.statusFilter };
    case 'SET_INVOICE_FILTER':
      return { ...state, invoiceFilter: action.invoiceFilter };
    case 'SET_RENEWAL_FILTER':
      return { ...state, renewalFilter: action.renewalFilter };
  }
}

export function usePolicyFilters(policies: IInsurancePolicy[]) {
  const [filters, dispatchFilter] = useReducer(filterReducer, initialFilterState);

  const filteredPolicies = useMemo(() => {
    let result = [...policies];

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(p => {
        const numberMatch = p.policyNumber.toLowerCase().includes(query);
        const nameMatch = p.customerName?.toLowerCase().includes(query) ?? false;
        return numberMatch || nameMatch;
      });
    }

    if (filters.typeFilter !== 'all') {
      result = result.filter(p => p.insuranceTypeId === filters.typeFilter);
    }

    if (filters.statusFilter !== 'all') {
      result = result.filter(p => p.status === filters.statusFilter);
    }

    if (filters.invoiceFilter !== 'all') {
      result = result.filter(p => (p.invoiceStatus ?? 'not_paid') === filters.invoiceFilter);
    }

    return result;
  }, [
    policies,
    filters.searchQuery,
    filters.typeFilter,
    filters.statusFilter,
    filters.invoiceFilter,
  ]);

  return {
    filters,
    filteredPolicies,
    dispatchFilter,
  };
}
